import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  Button,
  Typography,
  FormControlLabel,
  Box
} from "@mui/material";

const TermsOfServiceModal = ({ open, onAccept, theme }) => {
  const [accepted, setAccepted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const isDark = theme === "dark";

  const handleContinue = () => {
    setCompleted(true);
  };

  const handleOk = () => {
    onAccept();
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "75vw",
          height: "75vh",
          maxWidth: "75vw",
          maxHeight: "75vh",
          backgroundColor: isDark ? "#121212" : "#ffffff",
          color: isDark ? "#ffffff" : "#000000",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: isDark ? "#121212" : "#ffffff",
          borderBottom: "1px solid",
          borderColor: isDark ? "#333" : "#ddd",
          zIndex: 1,
        }}
      >
        Allpings SOFTWARE AS A SERVICE (SAAS) – TERMS OF SERVICE
      </DialogTitle>

      {/* Scrollable Content */}
      <DialogContent
        sx={{
          overflowY: "auto",
          maxHeight: "calc(100vh - 160px)",
          whiteSpace: "pre-line",
        }}
      >

        {completed ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" gutterBottom>
              You're all set
            </Typography>

            <Typography variant="body1" sx={{ maxWidth: 420 }}>
              Thanks for reviewing the Terms of Service. You can now continue using
              AllPings.
            </Typography>

            <Button
              variant="contained"
              sx={{ mt: 3 }}
              onClick={handleOk}
            >
              OK
            </Button>
          </Box>


        ) : (
          <>
            <Typography variant="body2">
              {`

Last Updated: 23 February 2026

These Terms of Service (“Terms”) govern access to and use of the AllPings software-as-a service platform (“Platform”) operated by MEmob PLUS FZ-LLC, a free zone limited liability company incorporated in Dubai Media City, UAE with commercial license number 40043, whose registered address is at P.O. Box 72184 Dubai Media City, Zee Building 2nd Floor, Office 206, Dubai, United Arab Emirates (“MEmob”, “we”, “us”).
By creating an account, clicking “I Accept”, or accessing or using the Platform, you agree to be legally bound by these Terms. If you do not agree, you must not access or use the Platform.

    1. DEFINITIONS

“Customer” means the legal entity that creates an account on the Platform. “User” means any individual authorized by Customer to access the Platform. “Services” means the functionalities made available through the Platform.
“Credits” means prepaid usage units with a monetary value, as defined on the Pricing Page.

“Customer Data” means all data uploaded to or generated through Customer’s use of the Platform.

“Confidential Information” means non-public information disclosed by one Party to the other.

“Business Hours” means 9:00 AM to 6:00 PM GST (Dubai Time) or IST (India Time), Monday to Friday, excluding public holidays.
“Statement of Work” or “SOW” means the document attached as Appendix A to these Terms, setting forth the specific scope of Services, configurations, volumes, and commercial parameters agreed between the Parties.

    2. ACCEPTANCE & ELECTRONIC AGREEMENT

        2.1 These Terms constitute a valid and legally binding Agreement between the Parties, formed and executed electronically in accordance with applicable laws governing electronic contracts and signatures.
        
        2.2 Acceptance is recorded by electronic means, including timestamp, IP address, and account credentials, and shall have the same force and legal effect as a handwritten signature and shall be deemed conclusive proof of the Customer’s Agreement to be bound by these Terms.

    3. ACCOUNT REGISTRATION & RESPONSIBILITY

        3.1 The Customer shall provide accurate, current, and complete registration information and shall promptly update such information as necessary to ensure its continued accuracy and completeness.
        
        3.2 The Customer shall be solely responsible for:
            (a) all acts and omissions occurring under its account, whether authorized or unauthorized;

            (b) ensuring that all Users comply with these Terms and any applicable policies;

            (c) maintaining the confidentiality and security of its account credentials.
        
            3.3 MEmob shall bear no liability for any unauthorized access to the Platform or Services resulting from the Customer’s failure to safeguard its account credentials, and the Customer shall indemnify and hold MEmob harmless from any loss or damage arising therefrom.

    4. LICENSE GRANT

        4.1 MEmob hereby grants to the Customer a limited, non-exclusive, non-transferable, and non-sublicensable license to access and use the Platform solely for the Customer’s internal business purposes in connection with media, advertising, and analytics, and only for the duration of an active subscription.
        
        4.2 All rights, title, and interest in and to the Platform and Services not expressly granted to the Customer under these Terms are reserved by MEmob.

    5. USAGE RESTRICTIONS

Customer shall not directly or indirectly:

    (a) Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code or underlying data of the Platform.
    
    (b) Resell, sublicense, lease, assign, distribute, or otherwise make the Platform or any portion thereof available to any Third Party.

    (c) Use the Platform for an unlawful, fraudulent or unauthorized purpose, or in violation of applicable laws or regulations.

    (d) Circumvent, disable, or otherwise interfere with any usage limits, billing mechanisms, or security features of the Platform.

    6. SUBSCRIPTION TERM & AUTO-RENEWAL

        6.1 Each Subscription shall commence on the date of the Customer’s first payment and shall continue for the initial term elected by the Customer during the sign-up process (“Initial Term”).
        
        6.2 Subscriptions are for annual terms and automatically renew for successive one-year renewal terms unless the Customer provides written notice of non-renewal at least sixty (60) days prior to the expiration of the then current term.
        
        6.3 The Customer may cancel renewal either through the Platform or by providing written notice to MEmob. Cancellation does not entitle the Customer to any refund of fees already paid for the current subscription term.

    7. PRICING, FEES & CREDITS

        7.1 Pricing shall be governed by the AllPings Pricing & Rate Card, as made available on the Platform and incorporated herein by reference.
        
        7.2 Fees payable by the Customer may include, without limitation:
            (a) Annual setup fees
            (b) Credit usage
            (c) Feature activations
            (d) Top-ups

        7.3 Credits:
            (a) shall be prepaid;
            (b) shall roll over on a monthly basis;
            (c) shall expire twelve (12) months from the date of issuance; and
            (d) shall be non-refundable under any and all circumstances.

        7.4 MEmob reserves the right to update or modify pricing upon thirty (30) days’ prior notice, which shall be deemed given when published on the Platform. Continued access to or use of the Platform following such notice shall constitute the Customer’s acceptance of the updated pricing. Customer shall have the right to terminate the subscription prior to the effective date of such change.
   
        8. PAYMENTS & BILLING

        8.1 All payments shall be processed via Stripe or such other authorized payment providers as designated by MEmob.
        
        8.2 The Customer hereby authorizes and consents to:
            (a) the recurring billing of all applicable fees;
            (b) the storage, transmission, and processing of its payment information by MEmob and its authorized payment providers.
        
        8.3 Failure by the Customer to pay any fees in a timely manner may result in the automatic suspension of access to the Platform and Services until payment is received. MEmob reserves the right to impose late charges or interest in accordance with applicable law.

    9. NO REFUNDS & FINANCIAL COMMITMENT

        9.1 All fees, including without limitation setup fees, credits, activations, and top-ups, shall be
strictly non-refundable under any and all circumstances.

        9.2 The Platform and Services are provided on an “AS IS” and “AS AVAILABLE” basis. No refunds or credits shall be issued in connection with dissatisfaction, performance expectations, or campaign outcomes.
        
        9.3 No trial period or statutory cooling-off period shall apply, unless expressly set forth in writing and agreed to by MEmob, to the maximum extent permitted under applicable law.

    10. DATA PROTECTION & SECURITY

        10.1 The Customer shall retain all rights, title, and interest in and to Customer Data.

        10.2 MEmob shall process Customer Data solely for the purpose of providing the Services and strictly in accordance with applicable data protection laws, including, where applicable, the General Data Protection Regulation (“GDPR”). For purposes of GDPR, the Customer shall be deemed the Data Controller and MEmob shall act as Data Processor. MEmob shall process Customer Data only on documented lawful instructions received from the Customer.
        
        10.3 Upon termination of the Agreement, Customer Data will be deleted within six (6) months, unless a longer retention period is required by applicable law or regulations.
        
        10.4 MEmob shall implement and maintain commercially reasonable technical and organizational security measures designed to protect Customer Data against unauthorized access, disclosure, alteration, or destruction, provided, however, that MEmob does not warrant or guarantee absolute security.

    11. CONFIDENTIALITY

        11.1 Each party (“Receiving Party”) shall maintain the confidentiality of all non-public, proprietary, or confidential information disclosed by the other party (“Disclosing Party”) in connection with these Terms (“Confidential Information”). The Receiving Party shall use such Confidential Information solely for the purpose of performing its obligations under these Terms and shall not disclose it to any Third Party except as expressly permitted herein.
        
        11.2 The obligations set forth in this Section shall not apply to information that:

            (a) is or becomes publicly available without breach of these Terms;

            (b) is lawfully received from a Third Party without restriction;

            (c) is independently developed by the Receiving Party without use of or reference to the Disclosing Party’s Confidential Information; or
            
            (d) is required to be disclosed pursuant to applicable law, regulation, or court order, provided that the Receiving Party gives prompt written notice to the Disclosing Party to allow it to seek protective measures.
        
            11.3 The confidentiality obligations set forth herein shall survive the termination or expiration of the Agreement and remain in full force and effect for a period of three (3) years thereafter.

    12. NON-SOLICITATION

During the term of this Agreement and for three (3) years following its termination, each Party agrees not to directly or indirectly solicit for employment, hire, or engage the employees or contractors of the other Party, whether for itself or any other person or entity. Each Party also agrees not to solicit or attempt to solicit the business of any Customer or client of the other Party that was directly introduced by the other Party under this Agreement, for services that compete with the services offered by the other Party.

    13. SUPPORT, MAINTENANCE AND SERVICE LEVELS

        13.1 Support shall be provided during Business Hours via email or via the designated support portal.
        
        13.2 Scheduled maintenance shall be communicated to the Customer at least 48 hours in advance, where reasonably practicable, and shall not exceed 8 hours per month. Such maintenance may result in temporary unavailability of the Platform or Services.
        
        13.3 MEmob shall use commercially reasonable efforts to ensure that the Platform is available at least 99.5% of the time in any calendar month, excluding scheduled maintenance and events beyond MEmob’s reasonable control.
        
        13.4 MEmob shall respond to support requests within the following timeframes:
            (a) Critical issues (Platform unavailable): within 2 hours.

            (b) High priority issues (severe impact on use): within 4 hours.

            (c) Normal issues (limited impact): within 24 hours.

    14. DISCLAIMER OF WARRANTIES

THE PLATFORM AND SERVICES ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS, TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, WITHOUT ANY WARRANTIES OF ANY KIND.
MEmob EXPRESSLY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING, WITHOUT LIMITATION, ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

    15. LIMITATION OF LIABILITY

        15.1 MEmob shall in no event be liable for any indirect, incidental, special, exemplary, or consequential damages, including but not limited to loss of profits, loss of data, or business interruption, arising out of or in connection with these Terms or the use of the Platform, whether based on contract, tort (including negligence), strict liability, or otherwise, even if MEmob has been advised of the possibility of such damages.
        
        15.2 The total aggregate liability of MEmob for any and all claims arising out of or relating to these Terms shall be strictly limited to the total fees actually paid by the Customer to MEmob during the twelve (12) months immediately preceding the event giving rise to the claim. This limitation shall not apply to liability arising from: (i) intellectual property infringement indemnities, (ii) breaches of data protection obligations, or (iii) gross negligence or willful misconduct.

    16. INDEMNIFICATION

        16.1 The Customer shall indemnify, defend, and hold harmless MEmob, its affiliates, directors, officers, employees, and agents from and against any and all claims, demands, actions, liabilities, damages, losses, costs, and expenses (including reasonable attorneys’ fees) arising out of or in connection with:
            (a) any misuse of the Platform by the Customer; or

            (b) any breach or violation of these Terms by the Customer.

        16.2 MEmob shall indemnify, defend, and hold harmless the Customer, its affiliates, directors, officers, employees, and agents from and against any and all Third Party claims, demands, actions, liabilities, damages, losses, costs, and expenses (including reasonable attorneys’ fees)
alleging that the Platform as provided by MEmob infringes or misappropriates any intellectual property rights of such Third Party.

    17. SUSPENSION & TERMINATION

        17.1 MEmob reserves the right, in its sole discretion, to suspend or terminate the Customer’s access to the Platform, in whole or in part, with immediate effect, upon the occurrence of any of the following events:
            (a) failure by the Customer to pay any fees when due;

            (b) breach of these Terms by the Customer, after giving ten (10) days’ written notice to cure such breach, except in cases of fraud or non-payment; or

            (c) use of the Platform by the Customer in any unlawful, fraudulent, or unauthorized manner.


        17.2 Effect of termination:

Upon termination of the Customer’s access to the Platform for any reason:

            (a) all rights granted to the Customer under these Terms shall immediately cease,

            (b) the Customer shall remain liable for all fees and charges accrued and payable up to the effective date of termination; and

            (c) any fees paid in advance shall be non-refundable.

    18. GOVERNING LAW & DISPUTE RESOLUTION

        18.1 These Terms shall be governed by, and construed in accordance with, the laws of
England and Wales.

        18.2 In the event of any dispute, controversy or claim arising out of or relating to this Agreement, or the breach, termination or invalidity thereof, the Parties agree to first attempt to resolve disputes through good-faith negotiations. Each Party agrees to use its best efforts to conduct any such negotiations promptly and efficiently.

        18.3 If the dispute cannot be resolved through negotiations within a period of thirty (30) days from the date such negotiations commence, both Parties agree that any dispute, controversy or claim arising out of or relating to this Agreement, or the breach, termination or invalidity thereof, shall be referred to and finally resolved by arbitration. The number of arbitrators shall be one. The place of arbitration proceedings shall be Dubai International Arbitration Centre in Dubai. The language of the arbitration shall be English. The Arbitration shall be conducted under the
Arbitration Rules of the Dubai International Arbitration Centre for Commercial Arbitration which are accordingly incorporated by reference into this arbitration clause.

    19. FORCE MAJEURE
Neither Party shall be liable for any failure or delay in performing its obligations under this Agreement (except for payment obligations) if such failure or delay results from events beyond the reasonable control of the affected Party, including but not limited to acts of God, natural disasters, internet or telecommunications failures, power outages, wars, terrorism, civil unrest, government actions, labor disputes, or other similar events. The affected Party shall promptly notify the other Party of the occurrence of such event and use commercially reasonable efforts to resume performance as soon as practicable.

    20. MODIFICATIONS TO TERMS

        20.1 MEmob reserves the right to amend, revise, or otherwise modify these Terms at any time, in its sole discretion.
        
        20.2 Any such modifications shall be effective upon posting the updated Terms on the Platform. MEmob shall indicate the date of the most recent revision at the top of the Terms.
        
        20.3 The Customer’s continued access to or use of the Platform following the effective date of any modifications shall constitute the Customer’s acceptance of the updated Terms. If the Customer does not agree to the modifications, the Customer must cease use of the Platform immediately.

    21. ENTIRE AGREEMENT
These Terms, together with referenced policies, pricing, and Appendix A – Statement of Work (SOW), constitute the entire Agreement between the Customer and MEmob regarding the Platform.

APPENDIX A – STATEMENT OF WORK (SOW)
    1. OBJECTIVE

The objective of this SOW is to set forth the specific scope of Services, configurations, volumes, and commercial parameters pursuant to which the Licensee shall be entitled to access and utilize the Licensed Product.

    2. SOW TERM
This SOW shall commence on the date the Customer accepts these Terms via Clickwrap and shall remain valid for twelve (12) months thereafter, unless terminated earlier in accordance with the Agreement. Renewal or extension of this SOW shall require the prior written agreement of both Parties.

    3. SCOPE OF SERVICES

        3.1 Provision of Services: The Licensor shall provide the Licensee with access to the Licensed Product, together with the following enabled services, as applicable during the Term of this Agreement and any applicable SOW:

            ▪ Audience Card Creation and/or Refresh
            ▪ Audience Card Activation
            ▪ Point of Interest (“POI”) Management
            ▪ Audience Reports and Analytics
            ▪ Cross-Match Feature
            ▪ Campaign Activation on Third Party Platforms

        3.2 Exclusions: Any services, deliverables, or features not expressly listed in Clause 3.1 above shall be excluded from the scope of this Agreement and any applicable SOW, unless otherwise agreed in a written amendment duly executed by authorized representatives of both Parties.

        3.3 No Implied Rights: For the avoidance of doubt, no rights or licenses are granted with respect to services or deliverables outside the express scope of Clause 3.1, and the Licensee shall not be entitled to rely on any implied rights or representations.

    4. SCOPE OF USE

The Licensed Product shall be enabled and made available for use by the Licensee in any territory where the Platform is lawfully offered, without limitation to specific territories.

    5. COMMERCIAL TERMS

        5.1 Setup Fees (Annual)
The Licensee shall pay an annual setup fee of USD 5,000 for the first country of activation, and USD 3,500 for each additional country.
The setup fees specified above shall be due and payable in accordance with the terms and conditions of the Agreement.
All such fees are non-refundable, irrespective of termination or non-utilization, unless otherwise expressly agreed in a written amendment duly executed by both Parties.

        5.2 Usage Fees (As Consumed)

Service
Unit Price (USD)
Audience Card Creation / Refresh
$350
Audience Card Activation (per platform)
$600
Usage fees shall be billed strictly on the basis of actual consumption, calculated through Credits as defined in the Agreement. All such fees shall be due and payable in accordance with the payment terms set forth in the Agreement.

        5.3 Monthly Media Spend & Cashback (If Applicable)

Cashback incentives shall apply solely on the basis of actual monthly media spend by the Licensee, in accordance with the following tiers:

Monthly Spend Tier
Cashback
$5,000
5%
$12,000
7%
$17,000
10%
Cashback amounts, if applicable, shall be calculated at the close of each calendar month and applied as a credit against future invoices, unless otherwise expressly agreed in writing by the Parties. No cashback shall accrue or be payable in respect of amounts below the minimum spend threshold or in the event of termination prior to month-end.

    6. CREDIT ALLOCATION & TOP-UPS
    • The minimum credit top-up amount shall be USD 1,000.

    • Credits shall roll over on a monthly basis, subject to the limitations set forth herein.

    • All credits shall automatically expire twelve (12) months from the date of issuance, without refund or carry-forward beyond such period.


No automatic credit refresh shall apply; any additional Credits must be purchased by the Licensee in accordance with the terms of this SOW and the Agreement.
    7. EXCLUSIONS

The following services are excluded from the scope of the Licensed Product and shall not be provided by the Licensor under this Agreement or any SOW:
    • Custom development

    • Data enrichment outside standard platform capabilities

    • Guaranteed campaign performance or outcomes

    • Managed media buying services


Any excluded services required by the Licensee shall be subject to a separate SOW or written amendment.

    8. DATA, INTELLECTUAL PROPERTY & LEGAL GOVERNANCE

        8.1 This SOW shall not amend, alter, or otherwise affect the following provisions of the Agreement:

            ▪ Intellectual property ownership
            ▪ Data protection obligations
            ▪ Confidentiality
            ▪ Limitation of liability
            ▪ Indemnification
            ▪ Termination rights

        8.2 All such matters shall remain governed exclusively by, and subject to, the terms and conditions of the Agreement.
   
        9. ORDER OF PRECEDENCE

In the event of any inconsistency or conflict between this SOW and any other document, the following order of precedence shall apply:

    I. This SOW (solely with respect to the scope of Services and commercial terms expressly set forth herein)

    II. These Terms of Service (Clickwrap Agreement)

    III. Pricing and Rate Card

    IV. Referenced Policies

For the avoidance of doubt, this SOW shall prevail only with respect to the scope of Services and commercial terms expressly defined herein, and all other matters shall remain governed by these Terms of Service and their incorporated documents.`}
            </Typography>
            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    sx={{
                      color: "#9e9e9e"
                    }}
                  />
                }
                label="I accept the above terms and conditions"
              />

              <Button
                variant="contained"
                disabled={!accepted}
                onClick={handleContinue}
              >
                Continue
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TermsOfServiceModal;
